import * as React from "react";
import axios from "axios";

import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { isRequestActive, loadAndTrack } from "redux-request-loading";

import { MedicalTypeId } from "@teamprevent/common/healthSurveillance";

import { ApplicationState } from "../../applicationState";

import { HealthSurveillanceApi } from "../healthSurveillanceApi";

type useMedicalTypesForAppointmentReturn =
    { medicalTypes: MedicalTypeId[]; isLoading: false; isError: false; refetch: () => Promise<void>; isLoaded: true }
    | { medicalTypes: undefined; isLoading: boolean; isError: boolean; refetch: () => Promise<void>; isLoaded: false };

/*
* This hook is used to load the medical types linked to a case on an appointment in a clinic.
* If you don't need the medical types straight away, you can pass `enable: false` to the hook to disable the loading of the medicals
* until you need it by calling `refetch()`
*/
export const useMedicalTypesForAppointment = (
    { clinicId, appointmentId }: { clinicId: string; appointmentId: string },
    { enable }: { enable?: boolean } = {}
): useMedicalTypesForAppointmentReturn => {
    const dispatch = useDispatch();
    const [medicalTypes, setMedicalTypes] = React.useState<MedicalTypeId[] | undefined>(undefined);
    const requestKey = `LOAD_MEDICAL_TYPES_FOR_APPOINTMENT_${clinicId}_${appointmentId}`;
    const isLoading = useSelector<ApplicationState, boolean>(state => isRequestActive(state, requestKey));
    const [isError, setIsError] = React.useState(false);
    const refetch = React.useCallback(async () => {
        try {
            setMedicalTypes(
                // TODO: Replace this with Redux Toolkit
                // @ts-expect-error TS2345
                await loadAndTrack(dispatch,requestKey, new HealthSurveillanceApi(axios).getMedicalTypesForAppointment(clinicId, appointmentId)));
            setIsError(false);
        } catch (e) {
            setIsError(true);
            throw e;
        }
    }, [clinicId, dispatch, appointmentId, requestKey]);
    React.useEffect(() => {
        if (enable === false
            || isLoading
            || isError
            || medicalTypes !== undefined) {
            return;
        }

        refetch();
    }, [enable, isError, isLoading, medicalTypes, refetch]);

    if (medicalTypes === undefined) {
        return { medicalTypes: undefined, isLoading, isError, refetch, isLoaded: false };
    } else {
        return { medicalTypes, isLoading: false, isError: false, refetch, isLoaded: true };
    }
};
